import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    Hub as ZoneIcon,
    Public as RegionIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import ZoneList from './zones/ZoneList';
import { zoneService } from '../../services/zoneService';
import { isZoneEngine, setRateEngine } from '../../services/rateEngine';

function ZoneManagement() {
    const [stats, setStats] = useState(null);
    const { showSuccess, showError } = useApiHandler();

    useEffect(() => {
        loadZoneStats();
    }, []);

    const loadZoneStats = async () => {
        try {
            const response = await zoneService.getZoneStats();
            
            if (response.success) {
                setStats(response.data);
                // Correct a cached engine value left over from an older session.
                setRateEngine(response.data.rate_engine);
            }
        } catch (error) {
            showError('Error loading zone statistics');
        }
    };

    const refreshStats = () => {
        loadZoneStats();
    };

    return (
        <DashboardLayout>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Zone Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {isZoneEngine()
                        ? 'Destination zones used for rate calculation. A booking is priced by the zone of its destination city.'
                        : 'Manage delivery zones and region mappings for rate calculations'}
                </Typography>
            </Box>

            {/* Statistics Cards */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <ZoneIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Zones</Typography>
                                </Box>
                                <Typography variant="h4" color="primary" gutterBottom>
                                    {stats.zones.total}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label={`${stats.zones.active} Active`} 
                                        color="success" 
                                        size="small" 
                                    />
                                    <Chip 
                                        label={`${stats.zones.inactive} Inactive`} 
                                        color="default" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* Under the zone engine a zone is defined by its cities, so
                        city coverage replaces the region and region-mapping cards. */}
                    {isZoneEngine() ? (
                        <Grid item xs={12} md={8}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <RegionIcon color="secondary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">Cities Covered</Typography>
                                    </Box>
                                    <Typography variant="h4" color="secondary" gutterBottom>
                                        {stats.cities?.assigned ?? 0} / {stats.cities?.total ?? 0}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {stats.cities?.unassigned > 0 ? (
                                            <Chip
                                                label={`${stats.cities.unassigned} without a zone, cannot be priced`}
                                                color="warning"
                                                size="small"
                                            />
                                        ) : (
                                            <Chip label="Every city is priceable" color="success" size="small" />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <RegionIcon color="secondary" sx={{ mr: 1 }} />
                                            <Typography variant="h6">Regions</Typography>
                                        </Box>
                                        <Typography variant="h4" color="secondary" gutterBottom>
                                            {stats.regions.total}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Chip
                                                label={`${stats.regions.active} Active`}
                                                color="success"
                                                size="small"
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <ZoneIcon color="info" sx={{ mr: 1 }} />
                                            <Typography variant="h6">Region Mappings</Typography>
                                        </Box>
                                        <Typography variant="h4" color="info.main" gutterBottom>
                                            {stats.mappings.total}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Chip
                                                label="Bidirectional"
                                                color="info"
                                                size="small"
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </>
                    )}
                </Grid>
            )}

            {/* Zone Management */}
            <Paper sx={{ width: '100%' }}>
                <ZoneList onDataChange={refreshStats} />
            </Paper>
        </DashboardLayout>
    );
}

export default ZoneManagement; 